import { type Dispatch, type SetStateAction, type CSSProperties } from "react";

import clsx from "clsx";

import { useIsDarkMode } from "@hooks/use-is-dark-mode";
import {
  type Cell,
  type ColumnDef,
  flexRender,
  type HeaderGroup,
} from "@tanstack/react-table";

interface TableCellProps<TData> {
  className?: string;
  cell: Cell<TData, unknown>;
  columns: ColumnDef<TData>[];
  columnIndex: number;
  headerGroups: HeaderGroup<TData>;
  style?: CSSProperties;
  shouldHighlightOnHover?: boolean;
  currentColumnIndexHovered?: number | null;
  setCurrentColumnIndexHovered?: Dispatch<SetStateAction<number | null>>;
}

export const TableCell = <TData,>({
  className,
  cell,
  columnIndex,
  headerGroups,
  style,
  shouldHighlightOnHover = false,
  currentColumnIndexHovered,
  setCurrentColumnIndexHovered,
}: TableCellProps<TData>) => {
  const isSticky = cell.column.getIsPinned();

  const [isDarkMode] = useIsDarkMode();

  const isLastLeftPinnedColumn =
    isSticky === "left" && cell.column.getIsLastColumn("left");

  let offset = 0;
  for (let i = 0; i < columnIndex; i++) {
    const previousHeader = headerGroups.headers[i];
    if (previousHeader.column.getIsPinned()) {
      offset += previousHeader.column.getSize();
    }
  }

  return (
    <td
      className={clsx(
        "bg-inherit h-inherit border-b-1 border-v2-tertiary",
        {
          sticky: isSticky,
          static: !isSticky,
          "z-1": isSticky,
          "hover:bg-v2-action-secondary": shouldHighlightOnHover,
          "bg-v2-action-tertiary":
            shouldHighlightOnHover && currentColumnIndexHovered === columnIndex,
        },
        className,
      )}
      style={{
        left: isSticky ? offset : "auto",
        top: 0,
        width: `calc(var(--col-${cell.column.id}-size) * 1px)`,
        ...(isSticky
          ? {
              minWidth: `calc(var(--col-${cell.column.id}-size) * 1px)`,
              maxWidth: `calc(var(--col-${cell.column.id}-size) * 1px)`,
            }
          : {}),
        ...style,
      }}
      onMouseEnter={() => {
        setCurrentColumnIndexHovered?.(columnIndex);
      }}
      onMouseLeave={() => {
        setCurrentColumnIndexHovered?.(null);
      }}
    >
      {
        flexRender(
          cell.column.columnDef.cell,
          cell.getContext(),
        ) as React.ReactNode
      }
      {isSticky && isLastLeftPinnedColumn && (
        <div className="absolute -right-0.5 top-0 bottom-0 w-1 bg-v2-overlay-shadow-200 backdrop-blur-[.5px]" />
      )}
    </td>
  );
};
